'use client';

import { parseAsString, useQueryState } from 'nuqs';
import { useOptimistic, useTransition } from 'react';

import { SelectField } from '@/vibes/soul/form/select-field';
import { Streamable, useStreamable } from '@/vibes/soul/lib/streamable';

export interface Option {
  label: string;
  value: string;
}

export function Sorting({
  label: streamableLabel,
  options: streamableOptions,
  paramName = 'sort',
  defaultValue = '',
  placeholder: streamablePlaceholder,
  variant = 'round',
}: {
  label?: Streamable<string | null>;
  options: Streamable<Option[]>;
  paramName?: string;
  defaultValue?: string;
  placeholder?: Streamable<string | null>;
  variant?: 'round' | 'rectangle' | 'minimal';
}) {
  const [param, setParam] = useQueryState(
    paramName,
    parseAsString.withDefault(defaultValue).withOptions({ shallow: false, history: 'push' }),
  );
  const [optimisticParam, setOptimisticParam] = useOptimistic(param);
  const [isPending, startTransition] = useTransition();
  const options = useStreamable(streamableOptions);
  const label = useStreamable(streamableLabel) ?? 'Sort';
  const placeholder = useStreamable(streamablePlaceholder) ?? 'Sort by';

  return (
    <SelectField
      hideLabel={variant === 'minimal' ? false : true}
      label={label}
      name={paramName}
      onValueChange={(value) => {
        startTransition(async () => {
          setOptimisticParam(value);
          await setParam(value);
        });
      }}
      options={options}
      pending={isPending}
      placeholder={placeholder}
      value={optimisticParam}
      variant={variant}
    />
  );
}

export function SortingSkeleton({ variant = 'round' }: { variant?: 'round' | 'rectangle' | 'minimal' } = {}) {
  return variant === 'minimal' ? (
    <div className="h-5 w-[8ch] animate-pulse rounded bg-contrast-100" />
  ) : (
    <div className="h-[50px] w-[12ch] animate-pulse rounded-full bg-contrast-100" />
  );
}
